<template>
  <div class="show-container" v-if="hasData">
    <div class="external-info">
      <div class="column-box" v-if="hasColumnPanel">
        <ul class="clearfix">
          <li class="cell" v-for="item in data.columnPanel.items">
            <div class="s-total-data" :class="item.highlight ? 'ongoing' : ''">
              <span class="num">{{ item.value }}</span>
              <span class="name">{{ item.label }}</span>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div :id="id" style="height: 100%;" :style="hasColumnPanel ? {width: '85%'} : {}"></div>
  </div>
  <show-no-data v-else></show-no-data>
</template>

<script>
  import eCharts from 'echarts'
  import 'echarts/theme/macarons'
  import analyseUtil from '../analyse-util'
  import ShowNoData from '../show-no-data'
  import {addResizeListener, removeResizeListener} from 'element-ui/src/utils/resize-event'

  export default {
    components: {
      ShowNoData
    },
    props: {
      data: {
        type: 'Object'
      }
    },
    name: 'bar-and-line-config',
    typeCode: '2',
    data() {
      return {
        id: analyseUtil.randomId(),
        myChart: {},
        valueAxis: [],
        itemAxis: [
          {
            type: 'category',
            data: null,
            axisPointer: {
              type: 'shadow'
            }
          }
        ],
        hasColumnPanel: this.data.columnPanel && this.data.columnPanel !== null && this.data.columnPanel.items.length > 0,
        options: {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            }
          },
          grid: {
            top: 10,
            left: 20,
            bottom: 10,
            containLabel: true
          },
          color: analyseUtil.COLORS,
          yAxis: [],
          xAxis: [],
          series: []
        },
        splitNumber: 8
      }
    },
    mounted() {
      if (this.hasData) {
        this.loadChart()
        addResizeListener(this.$el, this.resizeListener)
      }
    },
    beforeDestroy() {
      if (this.hasData) {
        removeResizeListener(this.$el, this.resizeListener)
      }
    },
    computed: {
      hasNoZero: function () {
        let hasNoZero = false
        for (let i = 0; i < this.data.series.length; i++) {
          for (let j = 0; j < this.data.series[i].data.length; j++) {
            hasNoZero = this.data.series[i].data[j] ? this.data.series[i].data[j] !== 0 : false
            if (hasNoZero) {
              return hasNoZero
            }
          }
        }
        return hasNoZero
      },
      hasData: function () {
        return this.data.item.items && this.data.item.items.length > 0 && this.data.series && this.data.series.length > 0 && this.hasNoZero
      },
      legendData: function () {
        let data = []
        for (let i = 0; i < this.data.series.length; i++) {
          data.push(this.data.series[i].name)
        }
        return data
      },
      isReverse: function () {
        return this.data.isReverse !== undefined && this.data.isReverse
      }
    },
    methods: {
      showDataZoom: function () {
        this.options.dataZoom = {
          inside: {
            filterMode: 'none'
          }
        }
      },
      // 获取平均线配置: 当index=0时设置position=start；反之设置position=end 解决问题双轴
      getAverageLineConfig: function (index) {
        return {
          symbol: ['none', 'none'],
          data: [{type: 'average', name: ''}],
          label: {
            formatter: function (param) {
              return param.data.value
            },
            position: index === 0 ? 'start' : 'end'
          }
        }
      },
      resizeListener: function () {
        this.$nextTick(() => {
          if (this.myChart !== null) {
            this.myChart.resize()
            this.myChart.clear()
            this.myChart.setOption(this.options)
          }
        })
      },
      getMaxValue: function (data) {
        let maxValue = 0
        for (let i = 0; i < data.length; i++) {
          maxValue = data[i] > maxValue ? data[i] : maxValue
        }
        return maxValue
      },
      loadChart: function () {
        this.myChart = eCharts.init(document.getElementById(this.id), 'shine')
        if (this.data.series && this.data.series.length > 1) {
          this.options.legend = {
            data: this.legendData,
            type: 'scroll',
            width: '70%'
          }
          this.options.grid.top = 40
        }
        this.itemAxis[0].data = this.data.item.items
        if (this.data.item.name) {
          this.itemAxis[0].name = this.data.item.name
          if (!this.isReverse) {
            this.options.grid.right = 100
          }
        }
        let valueMax = {}
        for (let i = 0; i < this.data.series.length; i++) {
          this.options.series.push({
            name: this.data.series[i].name,
            type: this.data.series[i].type,
            data: this.data.series[i].data,
            barMaxWidth: 15,
            itemStyle: {
              normal: {
                opacity: 0.8,
                barBorderRadius: [2, 2, 0, 0]
              },
              emphasis: {
                barBorderRadius: [2, 2, 0, 0]
              }
            },
            stack: this.data.series[i].group
          })
          this.options.series[i][this.isReverse ? 'xAxisIndex' : 'yAxisIndex'] = this.data.series[i].axisIndex
          let maxValue = this.getMaxValue(this.data.series[i].data)
          if (valueMax[this.data.series[i].axisIndex] === undefined || valueMax[this.data.series[i].axisIndex] < maxValue) {
            valueMax[this.data.series[i].axisIndex] = maxValue
          }
          // 展示平均线处理
          if (this.data.series[i].hasAverageLine !== undefined && this.data.series[i].hasAverageLine) {
            this.options.series[i].markLine = this.getAverageLineConfig(this.data.series[i].axisIndex)
          }
        }
        if (this.data.valueAxis === undefined || this.data.valueAxis === null) {
          this.valueAxis.push({
            type: 'value'
          })
        } else {
          let hasValueName = false
          for (let i = 0; i < this.data.valueAxis.length; i++) {
            this.valueAxis.push({
              type: 'value',
              name: this.data.valueAxis[i].name,
              axisLabel: {
                formatter: '{value} ' + (this.data.valueAxis[i].unit && this.data.valueAxis[i].unit !== null ? this.data.valueAxis[i].unit : '')
              }
            })
            hasValueName = hasValueName || this.data.valueAxis[i].name !== undefined
          }
          if (this.isReverse) {
            this.options.grid.right = 100
          } else {
            this.options.grid.top = 40
          }
        }
        if (this.isReverse) {
          this.options.xAxis = this.valueAxis
          this.options.yAxis = this.itemAxis
        } else {
          this.options.yAxis = this.valueAxis
          this.options.xAxis = this.itemAxis
          this.options.toolbox = {
            feature: {
              dataZoom: {
                yAxisIndex: 'none'
              },
              restore: {}
            },
            top: 40,
            orient: 'vertical',
            itemGap: 15,
            right: 25
          }
        }
        this.myChart.setOption(this.options)
        this.myChart.hideLoading()
      }
    }
  }
</script>

<style scoped>

  .column-box {
    position: absolute;
    right: 0;
    top: 10px;
  }

  .external-info {
    position: relative;
    width: 100%;
  }

  .cell {
    position: relative;
    text-align: center;
    padding: 15px 10px;
  }

  .s-total-data {
    padding: 0 5px;
  }

  .s-total-data.ongoing .num {
    color: #3b56ce;
  }

  .s-total-data.ongoing .name {
    color: #3b56ce;
    opacity: 0.8;
    filter: alpha(opacity=80);
  }

  .s-total-data .num {
    font-family: "Gill Sans", "Gill Sans MT", "Myriad Pro", "DejaVu Sans Condensed", Helvetica, Arial, "sans-serif";
    font-size: 20px;
    font-weight: 500;
    color: #222;
  }

  .s-total-data span {
    display: block;
    line-height: 1;
  }

  .s-total-data .name {
    margin-top: 10px;
    font-size: 12px;
    color: #999;
  }

  .s-total-data span {
    display: block;
    line-height: 1;
  }
</style>



// WEBPACK FOOTER //
// src/custom/components/page/analyse/show/components/bar-and-line-config.vue